import React, { useEffect, useState } from 'react';
import { Link as RouterLink, To, useNavigate } from 'react-router-dom';

import { Dropdown, DropdownOption } from '~/components/Dropdown';
import { views } from '~/controllers/views';
import { ECameraType } from '~/domain/ECameraType';
import { ICamera } from '~/domain/ICamera';
import { useRequestUrl } from '~/hooks/useRequestUrl';
import { MakeURLFriendly } from '~/pages/urls';

interface IDropdownOption {
  value: string;
  url: string;
  camera: string;
  code: string;
  name: string;
}

interface ICameraView {
  camera?: ICamera;
  cameras: ICamera[];
}

export const CameraView: React.FC<ICameraView> = (props) => {
  const { camera, cameras } = props;
  const requestUrl = useRequestUrl();
  const navigate = useNavigate();

  const mapDropdownOptions = (): IDropdownOption[] => {
    if (!cameras.length) {
      return [];
    }

    return views
      .map((view, index) => {
        const { name, types, code } = view;

        const dropdownCamera = cameras
          .filter((x) => types.includes(x.type))
          .map((x) =>
            MakeURLFriendly(
              x.type === ECameraType.FEATURE_RETAILER ? 'Retailer' : x.name
            )
          )[0];

        return {
          // if it's not on the manifest we default to the feature shots
          value: index > 0 ? dropdownCamera : MakeURLFriendly(views[0].name),
          url: requestUrl({
            camera: index > 0 ? dropdownCamera : null,
            waypoint: null,
          }),
          camera: dropdownCamera,
          code,
          name,
        };
      })
      .filter(({ camera, value }) => !!(camera && value));
  };

  const [dropdownOptions] = useState<IDropdownOption[]>(mapDropdownOptions());

  useEffect(() => {
    // On mount, go the URL for the default dropdown view, only if there's no camera selected
    if (!camera && dropdownOptions[0]?.url) {
      navigate(dropdownOptions[0].url);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // there is only a feature shot if the camera is of the FEATURE view type, or if there are feature shots in the cameras-manifest
  const hasFeatureShot =
    !!(camera && views[0].types.includes(camera.type)) ||
    !!cameras.find((value) => views[0].types.includes(value.type));
  const hasDealerShot =
    !!(camera && views[views.length - 1].types.includes(camera.type)) ||
    !!cameras.find((value) =>
      views[views.length - 1].types.includes(value.type)
    );

  function getDefaultValue() {
    if (!hasFeatureShot && cameras && cameras.length > 0) {
      return MakeURLFriendly(cameras[0].name).toLocaleLowerCase();
    } else if (hasFeatureShot) {
      return MakeURLFriendly(views[0].name).toLocaleLowerCase();
    } else if (hasDealerShot) {
      return MakeURLFriendly('retailer').toLocaleLowerCase();
    } else {
      // just a failsafe, since we should have at least one entry in the cameras-manifest
      return MakeURLFriendly(camera?.type).toLocaleLowerCase();
    }
  }

  const defaultValue = getDefaultValue();
  let dropdownValue = '';

  if (!camera) {
    // for some reason retailer shots come through without a camera. I guess this sortof makes sense since there isn't a camera that matches 'retailer'
    if (window.location.href.toLowerCase().indexOf('retailer') === -1) {
      dropdownValue = defaultValue;
    } else {
      dropdownValue = 'retailer';
    }
  } else {
    if (views[0].types.includes(camera.type)) {
      // Feature Shots is hardcoded as views[0] here.
      dropdownValue = MakeURLFriendly(views[0].name).toLocaleLowerCase();
    } else {
      // add case for retailer here
      if (camera.type === ECameraType.FEATURE_RETAILER) {
        dropdownValue = 'retailer';
      } else {
        dropdownValue = MakeURLFriendly(camera?.name).toLocaleLowerCase();
      }
    }
  }

  return (
    <Dropdown
      isLoading={!cameras.length}
      value={dropdownValue}
      valueLabel="View: "
      sx={{ minWidth: 260 }}
    >
      {dropdownOptions.map(
        ({ camera, value: optValue, code, url, name }, index) => {
          return (
            !!(camera && optValue) && (
              <DropdownOption
                key={code + '-' + index}
                to={url}
                value={optValue.toLocaleLowerCase()}
                component={
                  RouterLink as React.FC<{
                    value: string;
                    to: To;
                  }> as React.ElementType
                }
              >
                {name}
              </DropdownOption>
            )
          );
        }
      )}
    </Dropdown>
  );
};
